import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Building2, TrendingUp, TrendingDown, IndianRupee, Receipt, HandCoins, LogOut, Plus, X, Trash2, PieChart, MapPin, ShieldCheck, AlertCircle, Calendar, Play, Check, Banknote } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { ReportsPanel } from '../admin/AdminReports';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authCfg = () => {
  const token = localStorage.getItem('c24_token') || localStorage.getItem('c24_admin_token');
  return { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} };
};

const fmt = (n) => `₹${(Number(n) || 0).toLocaleString('en-IN')}`;

const EXPENSE_CATEGORIES = [
  { id: 'staff_salary',       label: 'Staff Salary' },
  { id: 'cleaner_payment',    label: 'Cleaner Payment' },
  { id: 'cleaner_salary',     label: 'Cleaner Salary' },
  { id: 'incentives',         label: 'Incentives' },
  { id: 'referral_bonus',     label: 'Referral Bonus' },
  { id: 'local_vendor',       label: 'Local Vendor' },
  { id: 'petty_cash',         label: 'Petty Cash' },
  { id: 'office_expense',     label: 'Office Expense' },
  { id: 'rent',               label: 'Office Rent' },
  { id: 'utilities',          label: 'Electricity / Utilities' },
  { id: 'fuel',               label: 'Fuel' },
  { id: 'marketing',          label: 'Marketing' },
  { id: 'advertisement',      label: 'Advertisement' },
  { id: 'internet',           label: 'Internet' },
  { id: 'software_expenses',  label: 'Software Expenses' },
  { id: 'insurance',          label: 'Insurance' },
  { id: 'bank_charges',       label: 'Bank Charges' },
  { id: 'gst_expense',        label: 'GST' },
  { id: 'tds_expense',        label: 'TDS' },
  { id: 'uniform_expenses',   label: 'Uniform Expenses' },
  { id: 'cleaning_materials', label: 'Cleaning Materials' },
  { id: 'equipment_purchase', label: 'Equipment Purchase' },
  { id: 'vehicle_expenses',   label: 'Vehicle Expenses' },
  { id: 'franchise_support',  label: 'Franchise Support' },
  { id: 'other',              label: 'Other' },
];

const TABS = ['Overview', 'Users', 'Reports', 'Franchise Wallet', 'Banking', 'Expenses', 'Cleaner Payouts', 'Payroll', 'P&L', 'Daily Collection', 'GST Sales'];

const FranchisePortal = () => {
  const nav = useNavigate();
  const { user, logout, hasPermission } = useApp();
  const canDeleteUsers = hasPermission('franchise_users', 'delete');
  const canEditUsers = hasPermission('franchise_users', 'edit');
  const canRecordExpenses = hasPermission('franchise_expenses', 'edit');
  const [tab, setTab] = useState('Overview');
  const [summary, setSummary] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [payables, setPayables] = useState(null);
  const [pnl, setPnl] = useState(null);
  const [daily, setDaily] = useState([]);
  const [gstReport, setGstReport] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [payrollDefaults, setPayrollDefaults] = useState(null);
  const [payrollMonth, setPayrollMonth] = useState(new Date().toISOString().slice(0, 7));
  const [payrollSummary, setPayrollSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddExp, setShowAddExp] = useState(false);
  const [expForm, setExpForm] = useState({ category: 'rent', amount: '', note: '' });
  const [showWalletTxn, setShowWalletTxn] = useState(null);
  const [walletTxnForm, setWalletTxnForm] = useState({ type: 'bonus', amount: '', note: '' });
  const [showAddEmp, setShowAddEmp] = useState(false);
  const [empForm, setEmpForm] = useState({ name: '', designation: '', base_salary: '', pan: '', bank_account: '', bank_ifsc: '' });
  // Banking (Phase 3 / iter-14)
  const [bankAccounts, setBankAccounts] = useState([]);
  const [vouchers, setVouchers] = useState([]);
  const [cashflow, setCashflow] = useState(null);
  const [bankingSubTab, setBankingSubTab] = useState('Accounts');
  const [showAddBank, setShowAddBank] = useState(false);
  const [bankForm, setBankForm] = useState({ name: '', type: 'bank', account_number: '', ifsc: '', opening_balance: '' });
  const [showAddVoucher, setShowAddVoucher] = useState(false);
  const [voucherForm, setVoucherForm] = useState({ voucher_type: 'payment', bank_account_id: '', amount: '', category: 'rent', payee: '', reference: '', note: '' });
  const [reconAccount, setReconAccount] = useState(null); // account_id for reconciliation view
  const [reconData, setReconData] = useState(null);
  // Users (iter-15b)
  const [franchiseUsers, setFranchiseUsers] = useState([]);
  const [userRoleFilter, setUserRoleFilter] = useState('all');
  const [showAddUser, setShowAddUser] = useState(false);
  const [userForm, setUserForm] = useState({ name: '', email: '', phone: '', role: 'customer', password: '' });
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);

  const isFranchiseOwner = user && user.role === 'franchise_admin';

  const load = async () => {
    if (!isFranchiseOwner) return;
    setLoading(true);
    try {
      const [s, e, p, plr, dc, gst, w, emp, pay, ba, vo, cf, us] = await Promise.all([
        axios.get(`${API}/franchise/accounts/summary`, authCfg()),
        axios.get(`${API}/franchise/expenses`, authCfg()),
        axios.get(`${API}/franchise/pending-payouts`, authCfg()),
        axios.get(`${API}/franchise/reports/pnl`, authCfg()),
        axios.get(`${API}/franchise/reports/daily-collection`, authCfg()),
        axios.get(`${API}/franchise/reports/gst-sales`, authCfg()),
        axios.get(`${API}/franchise/wallet`, authCfg()),
        axios.get(`${API}/franchise/employees`, authCfg()),
        axios.get(`${API}/franchise/payroll/${payrollMonth}`, authCfg()).catch(() => ({ data: null })),
        axios.get(`${API}/franchise/bank-accounts`, authCfg()),
        axios.get(`${API}/franchise/vouchers`, authCfg()),
        axios.get(`${API}/franchise/cashflow`, authCfg()),
        axios.get(`${API}/franchise/users`, authCfg()),
      ]);
      setSummary(s.data);
      setExpenses(e.data.expenses || []);
      setPayables(p.data);
      setPnl(plr.data);
      setDaily(dc.data.buckets || []);
      setGstReport(gst.data);
      setWallet(w.data);
      setEmployees(emp.data.employees || []);
      setPayrollDefaults(emp.data.payroll_defaults);
      setPayrollSummary(pay.data);
      setBankAccounts(ba.data.accounts || []);
      setVouchers(vo.data.vouchers || []);
      setCashflow(cf.data);
      setFranchiseUsers(us.data || []);
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.detail || 'Failed to load' });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user, payrollMonth]);

  if (!user) return <div className="p-8 text-center text-neutral-500">Please log in.</div>;
  if (!isFranchiseOwner) {
    const isSuperAdmin = user.role === 'admin';
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-5">
        <div className="bg-white border border-neutral-200 rounded-2xl p-8 max-w-md text-center">
          <ShieldCheck className="w-14 h-14 text-neutral-400 mx-auto" />
          <h1 className="mt-3 text-xl font-extrabold">{isSuperAdmin ? 'Use the Master ERP instead' : 'Franchise owner access required'}</h1>
          <p className="mt-2 text-sm text-neutral-500">
            {isSuperAdmin
              ? "Super Admins have global visibility. Head to the Master ERP dashboard to see consolidated data across all districts."
              : `Your account role (${user.role}) does not have access to this portal. If you're a franchise owner, contact Super Admin to update your role.`}
          </p>
          <button
            data-testid="franchise-portal-redirect-btn"
            onClick={() => nav(isSuperAdmin ? '/admin/erp' : '/home')}
            className="mt-5 w-full py-3 rounded-xl bg-[#2E7D32] text-white font-bold">
            {isSuperAdmin ? 'Open Master ERP' : 'Back to home'}
          </button>
        </div>
      </div>
    );
  }

  const addExpense = async () => {
    if (!expForm.amount) { setMsg({ ok: false, text: 'Amount required' }); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/franchise/expenses`, { ...expForm, amount: Number(expForm.amount) }, authCfg());
      setMsg({ ok: true, text: 'Expense recorded' });
      setShowAddExp(false);
      setExpForm({ category: 'rent', amount: '', note: '' });
      load();
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.detail || 'Failed to add' });
    } finally { setBusy(false); }
  };

  const deleteExpense = async (eid) => {
    if (!window.confirm('Delete this expense?')) return;
    try { await axios.delete(`${API}/franchise/expenses/${eid}`, authCfg()); load(); } catch {}
  };

  const submitWalletTxn = async () => {
    if (!walletTxnForm.amount) { setMsg({ ok: false, text: 'Amount required' }); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/franchise/cleaner-wallet-txn`, {
        cleaner_id: showWalletTxn, type: walletTxnForm.type,
        amount: Number(walletTxnForm.amount), note: walletTxnForm.note,
      }, authCfg());
      setMsg({ ok: true, text: `${walletTxnForm.type} recorded` });
      setShowWalletTxn(null);
      setWalletTxnForm({ type: 'bonus', amount: '', note: '' });
      load();
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.detail || 'Failed' });
    } finally { setBusy(false); }
  };

  const approvePayout = async (cleaner_id, name) => {
    if (!window.confirm(`Approve this week's payout for ${name}? A bank_transfer record will be created and matching cleaner_payable entries will be marked settled.`)) return;
    setBusy(true);
    try {
      const { data: r } = await axios.post(`${API}/franchise/approve-payout`, { cleaner_id }, authCfg());
      setMsg({ ok: true, text: `Payout of ₹${r.net_payout} approved for week ${r.week_start}` });
      load();
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.detail || 'Approve failed' });
    } finally { setBusy(false); }
  };

  const addEmployee = async () => {
    if (!empForm.name || !empForm.base_salary) { setMsg({ ok: false, text: 'Name & base salary required' }); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/franchise/employees`, {
        ...empForm, base_salary: Number(empForm.base_salary),
      }, authCfg());
      setMsg({ ok: true, text: `Employee ${empForm.name} added` });
      setShowAddEmp(false);
      setEmpForm({ name: '', designation: '', base_salary: '', pan: '', bank_account: '', bank_ifsc: '' });
      load();
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.detail || 'Failed' });
    } finally { setBusy(false); }
  };

  const deleteEmployee = async (eid) => {
    if (!window.confirm('Delete this employee? Past payroll entries remain in the ledger.')) return;
    try { await axios.delete(`${API}/franchise/employees/${eid}`, authCfg()); load(); } catch {}
  };

  const runPayroll = async () => {
    if (!window.confirm(`Run payroll for ${payrollMonth}? Already-run employees will be skipped.`)) return;
    setBusy(true);
    try {
      const { data: r } = await axios.post(`${API}/franchise/payroll/run`, { month: payrollMonth }, authCfg());
      setMsg({ ok: true, text: `Posted ${r.posted.length} salaries, skipped ${r.skipped_already_run.length} already-run` });
      load();
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.detail || 'Payroll run failed' });
    } finally { setBusy(false); }
  };

  const openPayslip = (empId) => {
    // Fetch payslip HTML with auth header, then open as blob
    axios.get(`${API}/franchise/payroll/payslip/${empId}?month=${payrollMonth}`, { ...authCfg(), responseType: 'text' })
      .then((r) => {
        const w = window.open('', '_blank');
        if (w) { w.document.write(r.data); w.document.close(); }
      })
      .catch((e) => setMsg({ ok: false, text: e?.response?.data?.detail || 'Payslip not generated yet — run payroll first' }));
  };

  // Banking handlers (iter-14)
  const addBankAccount = async () => {
    if (!bankForm.name) { setMsg({ ok: false, text: 'Account name required' }); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/franchise/bank-accounts`, {
        ...bankForm, opening_balance: Number(bankForm.opening_balance || 0),
      }, authCfg());
      setMsg({ ok: true, text: `${bankForm.type === 'bank' ? 'Bank' : 'Cash'} account added` });
      setShowAddBank(false);
      setBankForm({ name: '', type: 'bank', account_number: '', ifsc: '', opening_balance: '' });
      load();
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.detail || 'Failed' });
    } finally { setBusy(false); }
  };

  const deleteBankAccount = async (aid) => {
    if (!window.confirm('Delete this account? Fails if there are active vouchers.')) return;
    try {
      await axios.delete(`${API}/franchise/bank-accounts/${aid}`, authCfg());
      load();
    } catch (e) { setMsg({ ok: false, text: e?.response?.data?.detail || 'Delete failed' }); }
  };

  const submitVoucher = async () => {
    if (!voucherForm.bank_account_id || !voucherForm.amount) { setMsg({ ok: false, text: 'Account + amount required' }); return; }
    setBusy(true);
    try {
      const { data: r } = await axios.post(`${API}/franchise/vouchers`, {
        ...voucherForm, amount: Number(voucherForm.amount),
      }, authCfg());
      setMsg({ ok: true, text: `${r.voucher_type === 'payment' ? 'Payment' : 'Receipt'} voucher ${r.voucher_no} recorded` });
      setShowAddVoucher(false);
      setVoucherForm({ voucher_type: 'payment', bank_account_id: '', amount: '', category: 'rent', payee: '', reference: '', note: '' });
      load();
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.detail || 'Failed' });
    } finally { setBusy(false); }
  };

  const voidVoucher = async (vno) => {
    if (!window.confirm(`Void voucher ${vno}? A reversal entry will be posted.`)) return;
    try {
      await axios.post(`${API}/franchise/vouchers/${vno}/void`, {}, authCfg());
      setMsg({ ok: true, text: `${vno} voided` });
      load();
    } catch (e) { setMsg({ ok: false, text: e?.response?.data?.detail || 'Void failed' }); }
  };

  const openReconciliation = async (aid) => {
    try {
      const { data } = await axios.get(`${API}/franchise/reconciliation/${aid}`, authCfg());
      setReconAccount(aid);
      setReconData(data);
    } catch (e) { setMsg({ ok: false, text: e?.response?.data?.detail || 'Failed' }); }
  };

  const toggleCleared = async (entry) => {
    const path = entry.cleared ? 'unreconcile' : 'reconcile';
    try {
      await axios.post(`${API}/franchise/vouchers/${path}`, { entry_ids: [entry.id] }, authCfg());
      openReconciliation(reconAccount);
      load();
    } catch (e) { setMsg({ ok: false, text: e?.response?.data?.detail || 'Failed' }); }
  };

  // Users (iter-15b)
  const createFranchiseUser = async () => {
    if (!userForm.name || !userForm.email) { setMsg({ ok: false, text: 'Name & email required' }); return; }
    setBusy(true);
    try {
      const payload = { ...userForm };
      if (!payload.password) delete payload.password;
      const { data: r } = await axios.post(`${API}/franchise/users`, payload, authCfg());
      setMsg({ ok: true, text: `${userForm.role} "${r.user?.name || userForm.name}" created` });
      setShowAddUser(false);
      setUserForm({ name: '', email: '', phone: '', role: 'customer', password: '' });
      load();
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.detail || 'Failed to create user' });
    } finally { setBusy(false); }
  };

  const deleteFranchiseUser = async (uid, name) => {
    if (!window.confirm(`Remove user "${name}"? This deletes the account.`)) return;
    try {
      await axios.delete(`${API}/franchise/users/${uid}`, authCfg());
      setMsg({ ok: true, text: `Removed ${name}` });
      load();
    } catch (e) { setMsg({ ok: false, text: e?.response?.data?.detail || 'Delete failed' }); }
  };

  const filteredUsers = franchiseUsers.filter((u) => userRoleFilter === 'all' ? true : u.role === userRoleFilter);

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Header */}
      <header className="bg-white border-b border-neutral-200">
        <div className="max-w-[1300px] mx-auto px-5 md:px-8 py-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#E8F5E9] flex items-center justify-center">
            <Building2 className="w-5 h-5 text-[#2E7D32]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs uppercase tracking-wider text-neutral-500 font-semibold">Franchise Owner Portal</p>
            <p className="font-extrabold text-lg flex items-center gap-1 truncate">
              <MapPin className="w-4 h-4 text-[#2E7D32]" />
              <span data-testid="franchise-portal-district">{user.district || 'Unassigned district'}</span>
              <span className="text-xs font-mono text-neutral-400 ml-2">{user.franchise_id || '—'}</span>
            </p>
          </div>
          <button onClick={() => { logout && logout(); nav('/login'); }} className="inline-flex items-center gap-1.5 text-sm text-neutral-600 hover:text-red-600" data-testid="franchise-portal-logout">
            <LogOut className="w-4 h-4" /> Log out
          </button>
        </div>
        <div className="max-w-[1300px] mx-auto px-5 md:px-8 flex gap-1 overflow-x-auto pb-2">
          {TABS.map((t) => (
            <button key={t} data-testid={`franchise-tab-${t.toLowerCase().replace(/[^a-z]+/g, '-')}`}
              onClick={() => setTab(t)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${tab === t ? 'bg-[#2E7D32] text-white' : 'bg-white border border-neutral-200 text-neutral-600 hover:border-[#2E7D32]'}`}>
              {t}
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-[1300px] mx-auto px-5 md:px-8 py-6">
        {msg && (
          <div data-testid="franchise-msg" className={`mb-4 text-sm rounded-lg px-3 py-2 ${msg.ok ? 'bg-[#E8F5E9] text-[#1B5E20]' : 'bg-red-50 text-red-700'}`}>{msg.text}</div>
        )}
        {loading || !summary ? (
          <div className="text-center py-16"><Loader2 className="w-6 h-6 animate-spin mx-auto text-neutral-400" /></div>
        ) : (
          <>
            {tab === 'Overview' && (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Kpi label="District Income"     value={fmt(summary.totals.income)}             icon={IndianRupee} color="#2E7D32" bg="#E8F5E9" testid="franchise-income" />
                  <Kpi label="Expenses"            value={fmt(summary.totals.expenses)}           icon={TrendingDown} color="#DC2626" bg="#FEE2E2" testid="franchise-expenses" />
                  <Kpi label="Net Profit"          value={fmt(summary.totals.net_profit)}         icon={TrendingUp}   color="#7B1FA2" bg="#F3E5F5" testid="franchise-profit" />
                  <Kpi label="GST Collected"       value={fmt(summary.totals.gst_collected)}      icon={Receipt}      color="#D97706" bg="#FEF3C7" testid="franchise-gst" />
                  <Kpi label="Cleaner Payout Due"  value={fmt(summary.totals.cleaner_payout_due)} icon={HandCoins}    color="#0891B2" bg="#CFFAFE" testid="franchise-cleaner-due" />
                </div>

                <div className="grid md:grid-cols-2 gap-4 mt-6">
                  <Card title="Daily collection (last 30 days)" testid="franchise-daily">
                    <MiniBar rows={daily.slice(-14)} />
                  </Card>
                  <Card title="Expense breakdown" testid="franchise-exp-breakdown">
                    <ul className="divide-y divide-neutral-100">
                      {Object.entries(summary.expenses_by_category || {}).map(([k, v]) => (
                        <li key={k} className="px-4 py-2.5 flex items-center justify-between text-xs">
                          <span className="capitalize">{k.replace(/_/g, ' ')}</span>
                          <span className="font-bold text-red-600">₹{v.toLocaleString('en-IN')}</span>
                        </li>
                      ))}
                      {Object.keys(summary.expenses_by_category || {}).length === 0 && (
                        <li className="px-4 py-6 text-center text-neutral-400 text-xs">No expenses recorded yet.</li>
                      )}
                    </ul>
                  </Card>
                </div>
              </>
            )}

            {tab === 'Users' && (
              <div data-testid="franchise-users-panel">
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <div className="flex gap-1 bg-white border border-neutral-200 rounded-lg p-1">
                    {['all', 'customer', 'cleaner', 'staff'].map((r) => (
                      <button
                        key={r}
                        data-testid={`user-filter-${r}`}
                        onClick={() => setUserRoleFilter(r)}
                        className={`px-3 py-1 rounded-md text-xs font-semibold capitalize ${userRoleFilter === r ? 'bg-[#2E7D32] text-white' : 'text-neutral-600 hover:bg-neutral-100'}`}
                      >{r === 'all' ? 'All' : r + 's'}</button>
                    ))}
                  </div>
                  <span className="text-xs text-neutral-500 ml-2">{filteredUsers.length} user{filteredUsers.length === 1 ? '' : 's'}</span>
                  <button
                    data-testid="franchise-add-user-btn"
                    onClick={() => { setShowAddUser(true); setMsg(null); }}
                    disabled={!canEditUsers}
                    title={canEditUsers ? '' : 'You lack Edit permission for Franchise Users'}
                    className="ml-auto inline-flex items-center gap-1.5 bg-[#2E7D32] hover:bg-[#276D2A] text-white font-semibold text-sm px-4 py-2 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Plus className="w-4 h-4" /> Add user
                  </button>
                </div>

                {/* Credentials banner removed (security policy 2026-02) */}

                <div className="bg-white border border-neutral-200 rounded-2xl overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-neutral-50 border-b border-neutral-200 text-left">
                        <tr>
                          <Th>Name</Th><Th>Email</Th><Th>Phone</Th><Th>Role</Th><Th>Joined</Th><Th className="text-right">Action</Th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredUsers.map((u) => (
                          <tr key={u.user_id} data-testid={`franchise-user-row-${u.user_id}`} className="border-b border-neutral-100 hover:bg-neutral-50">
                            <td className="px-4 py-2.5">
                              <p className="text-xs font-semibold">{u.name}</p>
                              <p className="text-[10px] text-neutral-400 font-mono">{u.user_id}</p>
                            </td>
                            <td className="px-4 py-2.5 text-xs text-neutral-600">{u.email}</td>
                            <td className="px-4 py-2.5 text-xs text-neutral-600">{u.phone || '—'}</td>
                            <td className="px-4 py-2.5">
                              <span className={`inline-block text-[10px] font-bold px-2 py-0.5 rounded-full capitalize ${u.role === 'cleaner' ? 'bg-[#FFF3E0] text-[#EA580C]' : u.role === 'staff' ? 'bg-[#DBEAFE] text-[#1E88E5]' : u.role === 'franchise_admin' ? 'bg-[#F3E5F5] text-[#7B1FA2]' : 'bg-[#E8F5E9] text-[#2E7D32]'}`}>
                                {u.role}
                              </span>
                            </td>
                            <td className="px-4 py-2.5 text-[11px] text-neutral-500">{(u.created_at || '').slice(0, 10)}</td>
                            <td className="px-4 py-2.5 text-right">
                              {['customer', 'cleaner', 'staff'].includes(u.role) && u.user_id !== user.user_id && canDeleteUsers && (
                                <button
                                  data-testid={`franchise-user-delete-${u.user_id}`}
                                  onClick={() => deleteFranchiseUser(u.user_id, u.name)}
                                  className="text-red-500 hover:text-red-700"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                              {['customer', 'cleaner', 'staff'].includes(u.role) && u.user_id !== user.user_id && !canDeleteUsers && (
                                <span data-testid={`franchise-user-delete-locked-${u.user_id}`} className="text-neutral-300" title="Delete not permitted">🔒</span>
                              )}
                            </td>
                          </tr>
                        ))}
                        {filteredUsers.length === 0 && <tr><td colSpan={6} className="text-center text-neutral-400 py-10 text-xs">No {userRoleFilter === 'all' ? '' : userRoleFilter} users in your district yet. Click "Add user" to create one.</td></tr>}
                      </tbody>
                    </table>
                  </div>
                </div>
                <p className="mt-3 text-[11px] text-neutral-500 flex items-center gap-1"><ShieldCheck className="w-3 h-3 text-[#2E7D32]" /> You can create Customers, Cleaners, and Staff for your district. Set an initial password when creating each user — passwords are never displayed after creation.</p>
              </div>
            )}

            {tab === 'Reports' && (
              <div data-testid="franchise-reports-tab">
                <ReportsPanel scopeRole="franchise" />
              </div>
            )}

            {tab === 'Expenses' && (
              <div className="bg-white border border-neutral-200 rounded-2xl">
                <div className="px-4 py-3 border-b border-neutral-100 flex items-center gap-2">
                  <p className="font-bold text-sm flex-1">All expenses</p>
                  <button data-testid="franchise-add-expense-btn" onClick={() => setShowAddExp(true)} className="inline-flex items-center gap-1.5 bg-[#2E7D32] hover:bg-[#276D2A] text-white text-xs font-semibold px-3 py-1.5 rounded-lg">
                    <Plus className="w-3.5 h-3.5" /> Add expense
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-neutral-50 border-b border-neutral-200 text-left">
                      <tr><Th>Date</Th><Th>Category</Th><Th>Note</Th><Th className="text-right">Amount</Th><Th className="text-right">Action</Th></tr>
                    </thead>
                    <tbody>
                      {expenses.map((e) => (
                        <tr key={e.id} data-testid={`franchise-expense-${e.id}`} className="border-b border-neutral-100 hover:bg-neutral-50">
                          <td className="px-4 py-2.5 text-xs">{new Date(e.created_at).toLocaleDateString('en-IN')}</td>
                          <td className="px-4 py-2.5 text-xs capitalize">{(e.category || '').replace(/_/g, ' ')}</td>
                          <td className="px-4 py-2.5 text-xs text-neutral-600">{e.note || '—'}</td>
                          <td className="px-4 py-2.5 text-right font-bold text-red-600">₹{e.amount.toLocaleString('en-IN')}</td>
                          <td className="px-4 py-2.5 text-right">
                            <button onClick={() => deleteExpense(e.id)} className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
                          </td>
                        </tr>
                      ))}
                      {expenses.length === 0 && <tr><td colSpan="5" className="text-center py-8 text-neutral-400 text-xs">No expenses yet.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {tab === 'Franchise Wallet' && wallet && (
              <div className="space-y-4">
                <div className="bg-gradient-to-br from-[#2E7D32] to-[#1B5E20] text-white rounded-2xl p-5 flex items-center justify-between" data-testid="wallet-hero">
                  <div>
                    <p className="text-xs uppercase tracking-widest opacity-80 font-semibold">Franchise net balance</p>
                    <p className="mt-1 text-3xl font-extrabold" data-testid="wallet-net-balance">{fmt(wallet.summary.net_balance)}</p>
                    <p className="text-[11px] opacity-80 mt-1">Booking income − Cleaner payouts − Company charges − Marketing − Vendor payments − Refunds</p>
                  </div>
                  <div className="hidden md:flex flex-col items-end text-xs">
                    <p className="opacity-70">Pending collection</p>
                    <p className="font-bold text-lg" data-testid="wallet-pending-collection">{fmt(wallet.summary.pending_collection)}</p>
                    <p className="opacity-70 mt-0.5">{wallet.summary.pending_bookings_count} booking(s)</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Kpi label="Booking Income"      value={fmt(wallet.summary.booking_income)}    icon={IndianRupee} color="#2E7D32" bg="#E8F5E9" testid="wallet-booking-income" />
                  <Kpi label="Commission Earned"    value={fmt(wallet.summary.commission_earned)} icon={TrendingUp}  color="#059669" bg="#D1FAE5" testid="wallet-commission-earned" />
                  <Kpi label="Commission Paid"      value={fmt(wallet.summary.commission_paid)}   icon={HandCoins}   color="#0891B2" bg="#CFFAFE" testid="wallet-commission-paid" />
                  <Kpi label="Company Charges"      value={fmt(wallet.summary.company_charges)}   icon={Building2}   color="#7B1FA2" bg="#F3E5F5" testid="wallet-company-charges" />
                  <Kpi label="Marketing Charges"    value={fmt(wallet.summary.marketing_charges)} icon={TrendingDown} color="#EA580C" bg="#FFEDD5" testid="wallet-marketing" />
                  <Kpi label="Royalty (HQ)"          value={fmt(wallet.summary.royalty)}           icon={Receipt}     color="#D97706" bg="#FEF3C7" testid="wallet-royalty" />
                  <Kpi label="Customer Refunds"     value={fmt(wallet.summary.customer_refunds)}  icon={TrendingDown} color="#DC2626" bg="#FEE2E2" testid="wallet-refunds" />
                  <Kpi label="Vendor Payments"      value={fmt(wallet.summary.vendor_payment)}    icon={TrendingDown} color="#7C2D12" bg="#FEF3C7" testid="wallet-vendor" />
                </div>

                <Card title="Recent movements" testid="wallet-recent">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-neutral-50 border-b border-neutral-200 text-left">
                        <tr><Th>Date</Th><Th>Account</Th><Th>Note</Th><Th className="text-right">Amount</Th></tr>
                      </thead>
                      <tbody>
                        {(wallet.recent_movements || []).map((r) => (
                          <tr key={r.id} className="border-b border-neutral-100">
                            <td className="px-4 py-2 text-xs">{r.created_at?.slice(0, 10)}</td>
                            <td className="px-4 py-2 text-xs capitalize">{(r.account || '').replace(/_/g, ' ')}</td>
                            <td className="px-4 py-2 text-xs text-neutral-600 max-w-md truncate">{r.note || r.category || '—'}</td>
                            <td className={`px-4 py-2 text-right font-bold text-xs ${['franchise_earnings'].includes(r.account) ? 'text-[#2E7D32]' : 'text-neutral-700'}`}>₹{(r.amount || 0).toLocaleString('en-IN')}</td>
                          </tr>
                        ))}
                        {(wallet.recent_movements || []).length === 0 && <tr><td colSpan="4" className="text-center py-8 text-neutral-400 text-xs">No wallet movements yet.</td></tr>}
                      </tbody>
                    </table>
                  </div>
                </Card>
              </div>
            )}

            {tab === 'Payroll' && (
              <div className="space-y-4">
                <div className="bg-white border border-neutral-200 rounded-2xl p-4 flex flex-wrap items-center gap-3">
                  <div className="flex-1 min-w-[200px]">
                    <p className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Payroll month</p>
                    <input data-testid="payroll-month-input" type="month" value={payrollMonth} onChange={(e) => setPayrollMonth(e.target.value)} className="mt-1 px-3 py-2 rounded-lg border border-neutral-200 text-sm" />
                  </div>
                  {payrollDefaults && (
                    <div className="text-xs text-neutral-500">
                      <p className="font-semibold uppercase tracking-wider">Statutory defaults</p>
                      <p className="mt-1">PF {payrollDefaults.pf_pct}% · ESI {payrollDefaults.esi_pct}% · TDS {payrollDefaults.tds_pct}%</p>
                    </div>
                  )}
                  <button data-testid="payroll-add-employee-btn" onClick={() => setShowAddEmp(true)} className="inline-flex items-center gap-1.5 bg-white border border-neutral-200 hover:border-[#2E7D32] text-neutral-700 text-xs font-semibold px-3 py-2 rounded-lg">
                    <Plus className="w-3.5 h-3.5" /> Add employee
                  </button>
                  <button data-testid="payroll-run-btn" onClick={runPayroll} disabled={busy || employees.length === 0} className="inline-flex items-center gap-1.5 bg-[#2E7D32] hover:bg-[#276D2A] disabled:opacity-50 text-white text-xs font-semibold px-3 py-2 rounded-lg">
                    <Play className="w-3.5 h-3.5" /> Run payroll for {payrollMonth}
                  </button>
                </div>

                {payrollSummary && payrollSummary.rows && payrollSummary.rows.length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <Kpi label="Gross" value={fmt(payrollSummary.totals.gross)} icon={IndianRupee} color="#2E7D32" bg="#E8F5E9" testid="payroll-gross" />
                    <Kpi label="PF"    value={fmt(payrollSummary.totals.pf)}    icon={TrendingDown} color="#DC2626" bg="#FEE2E2" testid="payroll-pf" />
                    <Kpi label="ESI"   value={fmt(payrollSummary.totals.esi)}   icon={TrendingDown} color="#DC2626" bg="#FEE2E2" testid="payroll-esi" />
                    <Kpi label="TDS"   value={fmt(payrollSummary.totals.tds)}   icon={TrendingDown} color="#DC2626" bg="#FEE2E2" testid="payroll-tds" />
                    <Kpi label="Net paid" value={fmt(payrollSummary.totals.net)} icon={HandCoins}  color="#059669" bg="#D1FAE5" testid="payroll-net" />
                  </div>
                )}

                <div className="bg-white border border-neutral-200 rounded-2xl overflow-hidden">
                  <div className="px-4 py-3 border-b border-neutral-100"><p className="font-bold text-sm">Employees</p></div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-neutral-50 border-b border-neutral-200 text-left">
                        <tr><Th>Name</Th><Th>Designation</Th><Th className="text-right">Base salary</Th><Th className="text-right">This month</Th><Th className="text-right">Action</Th></tr>
                      </thead>
                      <tbody>
                        {employees.map((emp) => {
                          const run = (payrollSummary?.rows || []).find((r) => r.ref_id === emp.id);
                          return (
                            <tr key={emp.id} data-testid={`emp-row-${emp.id}`} className="border-b border-neutral-100 hover:bg-neutral-50">
                              <td className="px-4 py-3">
                                <p className="font-semibold text-xs">{emp.name}</p>
                                <p className="text-[10px] text-neutral-500">{emp.pan || 'PAN not set'}</p>
                              </td>
                              <td className="px-4 py-3 text-xs text-neutral-600">{emp.designation || '—'}</td>
                              <td className="px-4 py-3 text-right text-xs font-bold">₹{(emp.base_salary || 0).toLocaleString('en-IN')}</td>
                              <td className="px-4 py-3 text-right text-xs">
                                {run ? (
                                  <div>
                                    <span className="font-bold text-[#2E7D32]">₹{(run.payroll?.net || 0).toLocaleString('en-IN')}</span>
                                    <p className="text-[10px] text-neutral-400">−PF ₹{run.payroll?.pf} −ESI ₹{run.payroll?.esi} −TDS ₹{run.payroll?.tds}</p>
                                  </div>
                                ) : <span className="text-neutral-400">Not run</span>}
                              </td>
                              <td className="px-4 py-3 text-right whitespace-nowrap">
                                {run && (
                                  <button
                                    data-testid={`emp-payslip-btn-${emp.id}`}
                                    onClick={() => openPayslip(emp.id)}
                                    className="inline-flex items-center gap-1 bg-white border border-neutral-200 hover:border-[#2E7D32] hover:text-[#2E7D32] text-xs font-semibold px-2.5 py-1 rounded-lg mr-1">
                                    <Receipt className="w-3.5 h-3.5" /> Payslip
                                  </button>
                                )}
                                <button
                                  data-testid={`emp-del-btn-${emp.id}`}
                                  onClick={() => deleteEmployee(emp.id)}
                                  className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
                              </td>
                            </tr>
                          );
                        })}
                        {employees.length === 0 && <tr><td colSpan="5" className="text-center py-8 text-neutral-400 text-xs">No employees yet. Add one to run monthly payroll.</td></tr>}
                      </tbody>
                    </table>
                  </div>
                </div>
                <p className="text-[11px] text-neutral-500">Payroll runs are idempotent per (employee, month). Rerunning skips already-posted rows. Payslip opens in a new tab — press Print to save as PDF.</p>
              </div>
            )}

            {tab === 'Banking' && (
              <div className="space-y-4">
                {/* Sub-nav */}
                <div className="flex gap-1 overflow-x-auto">
                  {['Accounts', 'Vouchers', 'Reconciliation', 'Cash Flow'].map((s) => (
                    <button key={s} data-testid={`bank-subtab-${s.toLowerCase().replace(/\s/g, '-')}`}
                      onClick={() => setBankingSubTab(s)}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap ${bankingSubTab === s ? 'bg-[#2E7D32] text-white' : 'bg-white border border-neutral-200 text-neutral-600 hover:border-[#2E7D32]'}`}>
                      {s}
                    </button>
                  ))}
                </div>

                {bankingSubTab === 'Accounts' && (
                  <>
                    <div className="bg-white border border-neutral-200 rounded-2xl">
                      <div className="px-4 py-3 border-b border-neutral-100 flex items-center gap-2">
                        <p className="font-bold text-sm flex-1">Bank & Cash accounts</p>
                        <button data-testid="bank-add-account-btn" onClick={() => setShowAddBank(true)} className="inline-flex items-center gap-1.5 bg-[#2E7D32] hover:bg-[#276D2A] text-white text-xs font-semibold px-3 py-1.5 rounded-lg">
                          <Plus className="w-3.5 h-3.5" /> Add account
                        </button>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-neutral-50 border-b border-neutral-200 text-left">
                            <tr><Th>Name</Th><Th>Type</Th><Th>A/c / IFSC</Th><Th className="text-right">Opening</Th><Th className="text-right">Book bal.</Th><Th className="text-right">Cleared</Th><Th className="text-right">Action</Th></tr>
                          </thead>
                          <tbody>
                            {bankAccounts.map((a) => (
                              <tr key={a.id} data-testid={`bank-acct-${a.id}`} className="border-b border-neutral-100 hover:bg-neutral-50">
                                <td className="px-4 py-3">
                                  <p className="font-semibold text-xs">{a.name}</p>
                                  <p className="text-[10px] text-neutral-500 font-mono">{a.id}</p>
                                </td>
                                <td className="px-4 py-3 text-xs"><Pill color={a.type === 'bank' ? '#2E7D32' : '#D97706'} bg={a.type === 'bank' ? '#E8F5E9' : '#FEF3C7'}>{a.type}</Pill></td>
                                <td className="px-4 py-3 text-xs font-mono text-neutral-600">{a.account_number || '—'} <span className="text-neutral-400">{a.ifsc}</span></td>
                                <td className="px-4 py-3 text-right text-xs">₹{a.opening_balance.toLocaleString('en-IN')}</td>
                                <td className="px-4 py-3 text-right font-bold text-xs text-[#2E7D32]">₹{a.current_balance.toLocaleString('en-IN')}</td>
                                <td className="px-4 py-3 text-right text-xs text-neutral-500">₹{a.reconciled_balance.toLocaleString('en-IN')}</td>
                                <td className="px-4 py-3 text-right whitespace-nowrap">
                                  <button data-testid={`bank-recon-btn-${a.id}`} onClick={() => openReconciliation(a.id)} className="inline-flex items-center gap-1 bg-white border border-neutral-200 hover:border-[#2E7D32] hover:text-[#2E7D32] text-xs font-semibold px-2 py-1 rounded-lg mr-1">
                                    Reconcile
                                  </button>
                                  <button data-testid={`bank-del-btn-${a.id}`} onClick={() => deleteBankAccount(a.id)} className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
                                </td>
                              </tr>
                            ))}
                            {bankAccounts.length === 0 && <tr><td colSpan="7" className="text-center py-8 text-neutral-400 text-xs">No accounts yet. Add a bank or cash account to start posting vouchers.</td></tr>}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    <p className="text-[11px] text-neutral-500">Book balance = opening + Σ receipts − Σ payments (live). Cleared balance counts only reconciled vouchers.</p>
                  </>
                )}

                {bankingSubTab === 'Vouchers' && (
                  <>
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-bold flex-1">Payment & Receipt vouchers</p>
                      <button data-testid="voucher-add-btn" onClick={() => setShowAddVoucher(true)} disabled={bankAccounts.length === 0} className="inline-flex items-center gap-1.5 bg-[#2E7D32] hover:bg-[#276D2A] disabled:opacity-40 text-white text-xs font-semibold px-3 py-1.5 rounded-lg">
                        <Plus className="w-3.5 h-3.5" /> New voucher
                      </button>
                    </div>
                    {bankAccounts.length === 0 && <p className="text-xs text-red-600">Create a bank/cash account first.</p>}
                    <div className="bg-white border border-neutral-200 rounded-2xl overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-neutral-50 border-b border-neutral-200 text-left">
                          <tr><Th>#</Th><Th>Date</Th><Th>Type</Th><Th>Account</Th><Th>Category / Payee</Th><Th className="text-right">Amount</Th><Th className="text-right">Cleared</Th><Th className="text-right">Action</Th></tr>
                        </thead>
                        <tbody>
                          {vouchers.map((v) => {
                            const acct = bankAccounts.find(a => a.id === v.bank_account_id);
                            return (
                              <tr key={v.id} data-testid={`voucher-${v.voucher_no}${v.is_reversal ? '-rev' : ''}`} className={`border-b border-neutral-100 ${v.is_reversal ? 'bg-red-50/40 line-through' : ''}`}>
                                <td className="px-4 py-2.5 text-xs font-mono text-neutral-600">{v.voucher_no}</td>
                                <td className="px-4 py-2.5 text-xs">{v.created_at?.slice(0, 10)}</td>
                                <td className="px-4 py-2.5"><Pill color={v.voucher_type === 'payment' ? '#DC2626' : '#2E7D32'} bg={v.voucher_type === 'payment' ? '#FEE2E2' : '#E8F5E9'}>{v.voucher_type}</Pill></td>
                                <td className="px-4 py-2.5 text-xs text-neutral-600">{acct?.name || '—'}</td>
                                <td className="px-4 py-2.5 text-xs">
                                  <p className="capitalize">{(v.category || '').replace(/_/g, ' ')}</p>
                                  <p className="text-[10px] text-neutral-500">{v.payee}{v.reference ? ` · ${v.reference}` : ''}</p>
                                </td>
                                <td className={`px-4 py-2.5 text-right font-bold text-xs ${v.voucher_type === 'payment' ? 'text-red-600' : 'text-[#2E7D32]'}`}>{v.voucher_type === 'payment' ? '−' : '+'}₹{Math.abs(v.amount).toLocaleString('en-IN')}</td>
                                <td className="px-4 py-2.5 text-right text-xs">{v.cleared ? <span className="text-[#2E7D32] font-bold">✓ cleared</span> : <span className="text-neutral-400">pending</span>}</td>
                                <td className="px-4 py-2.5 text-right">
                                  {!v.is_reversal && (
                                    <button data-testid={`voucher-void-${v.voucher_no}`} onClick={() => voidVoucher(v.voucher_no)} className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                          {vouchers.length === 0 && <tr><td colSpan="8" className="text-center py-8 text-neutral-400 text-xs">No vouchers yet.</td></tr>}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}

                {bankingSubTab === 'Reconciliation' && (
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-neutral-500">Choose account</label>
                    <select data-testid="recon-account-select" value={reconAccount || ''} onChange={(e) => e.target.value && openReconciliation(e.target.value)} className="mt-1 w-full max-w-md px-3 py-2.5 rounded-lg border border-neutral-200 text-sm">
                      <option value="">Pick an account…</option>
                      {bankAccounts.map((a) => <option key={a.id} value={a.id}>{a.name} ({a.type})</option>)}
                    </select>

                    {reconData && (
                      <div className="mt-4 space-y-4">
                        <div className="grid grid-cols-3 gap-3">
                          <Kpi label="Book balance"       value={fmt(reconData.book_balance)}         icon={IndianRupee} color="#2E7D32" bg="#E8F5E9" testid="recon-book" />
                          <Kpi label="Reconciled balance" value={fmt(reconData.reconciled_balance)}    icon={ShieldCheck} color="#059669" bg="#D1FAE5" testid="recon-reconciled" />
                          <Kpi label="Unreconciled delta" value={fmt(reconData.unreconciled_delta)}   icon={AlertCircle} color="#D97706" bg="#FEF3C7" testid="recon-delta" />
                        </div>
                        <div className="bg-white border border-neutral-200 rounded-2xl overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead className="bg-neutral-50 border-b border-neutral-200 text-left">
                              <tr><Th>Voucher</Th><Th>Date</Th><Th>Type</Th><Th className="text-right">Amount</Th><Th className="text-right">Cleared?</Th></tr>
                            </thead>
                            <tbody>
                              {(reconData.vouchers || []).filter(v => !v.is_reversal).map((v) => (
                                <tr key={v.id} data-testid={`recon-row-${v.voucher_no}`} className="border-b border-neutral-100">
                                  <td className="px-4 py-2 text-xs font-mono">{v.voucher_no}</td>
                                  <td className="px-4 py-2 text-xs">{v.created_at?.slice(0, 10)}</td>
                                  <td className="px-4 py-2"><Pill color={v.voucher_type === 'payment' ? '#DC2626' : '#2E7D32'} bg={v.voucher_type === 'payment' ? '#FEE2E2' : '#E8F5E9'}>{v.voucher_type}</Pill></td>
                                  <td className={`px-4 py-2 text-right font-bold text-xs ${v.voucher_type === 'payment' ? 'text-red-600' : 'text-[#2E7D32]'}`}>{v.voucher_type === 'payment' ? '−' : '+'}₹{Math.abs(v.amount).toLocaleString('en-IN')}</td>
                                  <td className="px-4 py-2 text-right">
                                    <button data-testid={`recon-toggle-${v.voucher_no}`} onClick={() => toggleCleared(v)} className={`w-5 h-5 rounded border-2 flex items-center justify-center ${v.cleared ? 'bg-[#2E7D32] border-[#2E7D32]' : 'bg-white border-neutral-300 hover:border-[#2E7D32]'}`}>
                                      {v.cleared && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                                    </button>
                                  </td>
                                </tr>
                              ))}
                              {(reconData.vouchers || []).length === 0 && <tr><td colSpan="5" className="text-center py-8 text-neutral-400 text-xs">No vouchers on this account.</td></tr>}
                            </tbody>
                          </table>
                        </div>
                        <p className="text-[11px] text-neutral-500">Tick each voucher as it appears in your bank statement. Delta of ₹0 = books match the bank.</p>
                      </div>
                    )}
                  </div>
                )}

                {bankingSubTab === 'Cash Flow' && cashflow && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-3 gap-3">
                      <Kpi label="Inflow (30d)"  value={fmt(cashflow.totals.inflow)}  icon={TrendingUp}   color="#2E7D32" bg="#E8F5E9" testid="cf-inflow" />
                      <Kpi label="Outflow (30d)" value={fmt(cashflow.totals.outflow)} icon={TrendingDown} color="#DC2626" bg="#FEE2E2" testid="cf-outflow" />
                      <Kpi label="Net"           value={fmt(cashflow.totals.net)}     icon={PieChart}    color={cashflow.totals.net >= 0 ? '#2E7D32' : '#DC2626'} bg={cashflow.totals.net >= 0 ? '#E8F5E9' : '#FEE2E2'} testid="cf-net" />
                    </div>
                    <Card title="Money in vs out — last 30 days" testid="cf-chart">
                      <CashFlowChart buckets={cashflow.buckets || []} />
                    </Card>
                  </div>
                )}
              </div>
            )}

            {tab === 'Cleaner Payouts' && payables && (
              <div>
                <div className="bg-[#E8F5E9] border border-[#C8E6C9] rounded-2xl px-4 py-3 mb-4 flex items-center gap-2 text-xs" data-testid="payouts-week-banner">
                  <CalendarBanner start={payables.week_start} end={payables.week_end} />
                </div>
                <div className="bg-white border border-neutral-200 rounded-2xl overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-neutral-50 border-b border-neutral-200 text-left">
                        <tr>
                          <Th>Cleaner</Th><Th>Earned</Th><Th>Bonus</Th><Th>Incentive</Th>
                          <Th>Penalty</Th><Th>Advance</Th><Th className="text-right">Net payout</Th>
                          <Th className="text-right">Action</Th>
                        </tr>
                      </thead>
                      <tbody>
                        {payables.rows.map((r) => (
                          <tr key={r.cleaner_id} data-testid={`payable-${r.cleaner_id}`} className="border-b border-neutral-100 hover:bg-neutral-50">
                            <td className="px-4 py-3">
                              <p className="font-semibold text-xs">{r.cleaner?.name || '—'}</p>
                              <p className="text-[10px] text-neutral-500">{r.cleaner?.email}</p>
                            </td>
                            <td className="px-4 py-3 text-xs font-bold text-[#2E7D32]">₹{r.earned.toLocaleString('en-IN')}</td>
                            <td className="px-4 py-3 text-xs text-[#7B1FA2]">₹{r.bonus.toLocaleString('en-IN')}</td>
                            <td className="px-4 py-3 text-xs text-[#7B1FA2]">₹{r.incentives.toLocaleString('en-IN')}</td>
                            <td className="px-4 py-3 text-xs text-red-600">₹{r.penalty.toLocaleString('en-IN')}</td>
                            <td className="px-4 py-3 text-xs text-red-600">₹{r.advance.toLocaleString('en-IN')}</td>
                            <td className="px-4 py-3 text-right font-extrabold text-[#2E7D32]" data-testid={`payable-net-${r.cleaner_id}`}>₹{r.net_payout.toLocaleString('en-IN')}</td>
                            <td className="px-4 py-3 text-right whitespace-nowrap">
                              <button
                                data-testid={`payable-txn-btn-${r.cleaner_id}`}
                                onClick={() => setShowWalletTxn(r.cleaner_id)}
                                className="inline-flex items-center gap-1 bg-white border border-neutral-200 hover:border-[#2E7D32] text-neutral-700 text-xs font-semibold px-2 py-1 rounded-lg mr-1">
                                <Plus className="w-3 h-3" /> Adjust
                              </button>
                              <button
                                data-testid={`payable-approve-btn-${r.cleaner_id}`}
                                onClick={() => approvePayout(r.cleaner_id, r.cleaner?.name || 'cleaner')}
                                disabled={busy || r.net_payout <= 0}
                                className="inline-flex items-center gap-1 bg-[#2E7D32] hover:bg-[#276D2A] disabled:opacity-40 text-white text-xs font-semibold px-2.5 py-1 rounded-lg">
                                Approve
                              </button>
                            </td>
                          </tr>
                        ))}
                        {payables.rows.length === 0 && <tr><td colSpan="8" className="text-center py-8 text-neutral-400 text-xs">No cleaner activity this week.</td></tr>}
                      </tbody>
                    </table>
                  </div>
                </div>
                <p className="mt-3 text-[11px] text-neutral-500">Weekly Settlement (Mon–Sun). Approve creates a bank_transfer entry and marks the week's cleaner_payable entries as settled — idempotent per cleaner + week.</p>
              </div>
            )}

            {tab === 'GST Sales' && gstReport && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Kpi label="Taxable Value" value={fmt(gstReport.totals.taxable_value)}  icon={IndianRupee} color="#2E7D32" bg="#E8F5E9" testid="gst-taxable" />
                  <Kpi label="CGST"          value={fmt(gstReport.totals.cgst)}           icon={Receipt}     color="#D97706" bg="#FEF3C7" testid="gst-cgst" />
                  <Kpi label="SGST"          value={fmt(gstReport.totals.sgst)}           icon={Receipt}     color="#D97706" bg="#FEF3C7" testid="gst-sgst" />
                  <Kpi label="Invoice Total" value={fmt(gstReport.totals.invoice_total)}  icon={PieChart}    color="#7B1FA2" bg="#F3E5F5" testid="gst-total" />
                </div>
                <Card title="GST Sales — booking-level breakdown" testid="gst-report-table">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-neutral-50 border-b border-neutral-200 text-left">
                        <tr><Th>Date</Th><Th>Booking</Th><Th>Service</Th><Th className="text-right">Taxable</Th><Th className="text-right">CGST</Th><Th className="text-right">SGST</Th><Th className="text-right">Total</Th></tr>
                      </thead>
                      <tbody>
                        {gstReport.rows.map((r) => (
                          <tr key={r.booking_id} data-testid={`gst-row-${r.booking_id}`} className="border-b border-neutral-100">
                            <td className="px-3 py-2 text-xs">{r.date}</td>
                            <td className="px-3 py-2 text-xs font-mono text-neutral-600">{r.booking_id}</td>
                            <td className="px-3 py-2 text-xs capitalize">{(r.service_category || '').replace(/_/g, ' ')}</td>
                            <td className="px-3 py-2 text-right text-xs font-bold text-[#2E7D32]">₹{r.taxable_value.toLocaleString('en-IN')}</td>
                            <td className="px-3 py-2 text-right text-xs text-[#D97706]">₹{r.cgst.toLocaleString('en-IN')}</td>
                            <td className="px-3 py-2 text-right text-xs text-[#D97706]">₹{r.sgst.toLocaleString('en-IN')}</td>
                            <td className="px-3 py-2 text-right text-xs font-bold">₹{r.invoice_total.toLocaleString('en-IN')}</td>
                          </tr>
                        ))}
                        {gstReport.rows.length === 0 && <tr><td colSpan="7" className="text-center py-8 text-neutral-400 text-xs">No GST-taxable sales yet.</td></tr>}
                      </tbody>
                    </table>
                  </div>
                </Card>
                <Card title="By service category (taxable value)" testid="gst-by-category">
                  <ul className="divide-y divide-neutral-100">
                    {Object.entries(gstReport.by_category || {}).map(([k, v]) => (
                      <li key={k} className="px-4 py-2.5 flex items-center justify-between text-xs">
                        <span className="capitalize">{k.replace(/_/g, ' ')}</span>
                        <span className="font-bold text-[#2E7D32]">₹{v.toLocaleString('en-IN')}</span>
                      </li>
                    ))}
                    {Object.keys(gstReport.by_category || {}).length === 0 && <li className="px-4 py-6 text-center text-neutral-400 text-xs">No data</li>}
                  </ul>
                </Card>
              </div>
            )}

            {tab === 'P&L' && pnl && (
              <div className="grid md:grid-cols-2 gap-4">
                <Card title="Income" testid="pnl-income">
                  <ul className="px-4 py-3 text-sm">
                    <li className="flex justify-between py-1.5"><span>Franchise earnings from bookings</span><span className="font-bold text-[#2E7D32]">₹{(pnl.income || 0).toLocaleString('en-IN')}</span></li>
                    <li className="flex justify-between py-1.5"><span>GST collected (HQ liability)</span><span className="font-bold text-[#D97706]">₹{(pnl.gst_collected || 0).toLocaleString('en-IN')}</span></li>
                    <li className="flex justify-between py-1.5 border-t border-neutral-200 mt-2 font-extrabold"><span>Total income</span><span>₹{(pnl.income || 0).toLocaleString('en-IN')}</span></li>
                  </ul>
                </Card>
                <Card title="Expenses" testid="pnl-expenses">
                  <ul className="px-4 py-3 text-sm">
                    {Object.entries(pnl.expenses_by_category || {}).map(([k, v]) => (
                      <li key={k} className="flex justify-between py-1.5"><span className="capitalize">{k.replace(/_/g, ' ')}</span><span className="font-bold text-red-600">₹{v.toLocaleString('en-IN')}</span></li>
                    ))}
                    <li className="flex justify-between py-1.5 border-t border-neutral-200 mt-2 font-extrabold"><span>Total expenses</span><span>₹{(pnl.total_expenses || 0).toLocaleString('en-IN')}</span></li>
                    <li className={`flex justify-between py-2 mt-2 rounded-lg px-2 font-extrabold ${pnl.net_profit >= 0 ? 'bg-[#E8F5E9] text-[#1B5E20]' : 'bg-red-50 text-red-700'}`}><span>Net profit</span><span data-testid="pnl-net-profit">₹{(pnl.net_profit || 0).toLocaleString('en-IN')}</span></li>
                  </ul>
                </Card>
              </div>
            )}

            {tab === 'Daily Collection' && (
              <Card title="Daily collection (last 30 days)" testid="daily-collection-card">
                <table className="w-full text-sm">
                  <thead className="bg-neutral-50 border-b border-neutral-200 text-left">
                    <tr><Th>Date</Th><Th className="text-right">Amount collected</Th></tr>
                  </thead>
                  <tbody>
                    {daily.slice().reverse().map((d) => (
                      <tr key={d.date} className="border-b border-neutral-100"><td className="px-4 py-2 text-xs">{d.date}</td><td className="px-4 py-2 text-right font-bold text-[#2E7D32]">₹{d.amount.toLocaleString('en-IN')}</td></tr>
                    ))}
                    {daily.length === 0 && <tr><td colSpan="2" className="text-center py-8 text-neutral-400 text-xs">No collections yet.</td></tr>}
                  </tbody>
                </table>
              </Card>
            )}

            <p className="mt-6 text-[11px] text-neutral-500 flex items-center gap-1"><ShieldCheck className="w-3 h-3 text-[#2E7D32]" /> You only see data for your assigned district. HQ has global visibility.</p>
          </>
        )}
      </main>

      {showAddUser && (
        <Modal onClose={() => setShowAddUser(false)} title="Add user to your franchise">
          <p className="text-xs text-neutral-500 mb-3">Creates a Customer, Cleaner, or Staff account tagged to {user.district}. You must set the initial password below.</p>
          <label className="block text-xs font-semibold text-neutral-600">Role</label>
          <select data-testid="user-form-role" value={userForm.role} onChange={(e) => setUserForm({ ...userForm, role: e.target.value })} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-neutral-200 text-sm">
            <option value="customer">Customer</option>
            <option value="cleaner">Cleaner</option>
            <option value="staff">Staff / Supervisor</option>
          </select>
          <label className="mt-3 block text-xs font-semibold text-neutral-600">Full name</label>
          <input data-testid="user-form-name" value={userForm.name} onChange={(e) => setUserForm({ ...userForm, name: e.target.value })} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-neutral-200 text-sm" placeholder="Ravi Kumar" />
          <label className="mt-3 block text-xs font-semibold text-neutral-600">Email</label>
          <input data-testid="user-form-email" type="email" value={userForm.email} onChange={(e) => setUserForm({ ...userForm, email: e.target.value })} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-neutral-200 text-sm" placeholder="ravi@example.com" />
          <label className="mt-3 block text-xs font-semibold text-neutral-600">Phone (optional)</label>
          <input data-testid="user-form-phone" value={userForm.phone} onChange={(e) => setUserForm({ ...userForm, phone: e.target.value.replace(/[^0-9+]/g, '') })} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-neutral-200 text-sm" placeholder="9876543210" />
          <label className="mt-3 block text-xs font-semibold text-neutral-600">Password (required, min 6 chars)</label>
          <input data-testid="user-form-password" type="text" value={userForm.password} onChange={(e) => setUserForm({ ...userForm, password: e.target.value })} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-neutral-200 text-sm font-mono" placeholder="Type an initial password" required />
          <button data-testid="user-form-submit" onClick={createFranchiseUser} disabled={busy || !userForm.password || userForm.password.length < 6} className="mt-4 w-full py-2.5 rounded-lg bg-[#2E7D32] hover:bg-[#276D2A] disabled:opacity-50 text-white font-semibold text-sm">
            {busy ? 'Creating…' : 'Create user'}
          </button>
        </Modal>
      )}

      {showAddExp && (
        <Modal onClose={() => setShowAddExp(false)} title="Record an expense">
          <label className="block text-xs font-semibold text-neutral-600">Category</label>
          <select data-testid="expense-category" value={expForm.category} onChange={(e) => setExpForm({ ...expForm, category: e.target.value })} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-neutral-200 text-sm">
            {EXPENSE_CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
          <label className="mt-3 block text-xs font-semibold text-neutral-600">Amount (₹)</label>
          <input data-testid="expense-amount" type="text" inputMode="numeric" value={expForm.amount} onChange={(e) => setExpForm({ ...expForm, amount: e.target.value.replace(/[^0-9]/g, '') })} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-neutral-200 text-sm" placeholder="1500" />
          <label className="mt-3 block text-xs font-semibold text-neutral-600">Note (optional)</label>
          <input data-testid="expense-note" value={expForm.note} onChange={(e) => setExpForm({ ...expForm, note: e.target.value })} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-neutral-200 text-sm" placeholder="Rent for March" />
          <button data-testid="expense-submit" onClick={addExpense} disabled={busy} className="mt-4 w-full py-2.5 rounded-lg bg-[#2E7D32] hover:bg-[#276D2A] disabled:opacity-50 text-white font-semibold text-sm">
            {busy ? 'Saving…' : 'Record expense'}
          </button>
        </Modal>
      )}

      {showWalletTxn && (
        <Modal onClose={() => setShowWalletTxn(null)} title="Record cleaner adjustment">
          <p className="text-xs text-neutral-500 mb-3">Adds a bonus / incentive / penalty / advance / cancellation charge to this cleaner's wallet.</p>
          <label className="block text-xs font-semibold text-neutral-600">Type</label>
          <select data-testid="wallet-txn-type" value={walletTxnForm.type} onChange={(e) => setWalletTxnForm({ ...walletTxnForm, type: e.target.value })} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-neutral-200 text-sm">
            <option value="bonus">Bonus (adds to net payout)</option>
            <option value="incentive">Incentive (adds to net payout)</option>
            <option value="penalty">Penalty (deducts from net payout)</option>
            <option value="advance">Advance received (deducts from net payout)</option>
            <option value="cancellation">Cancellation charge (deducts)</option>
          </select>
          <label className="mt-3 block text-xs font-semibold text-neutral-600">Amount (₹)</label>
          <input data-testid="wallet-txn-amount" type="text" inputMode="numeric" value={walletTxnForm.amount} onChange={(e) => setWalletTxnForm({ ...walletTxnForm, amount: e.target.value.replace(/[^0-9]/g, '') })} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-neutral-200 text-sm" placeholder="500" />
          <label className="mt-3 block text-xs font-semibold text-neutral-600">Note</label>
          <input data-testid="wallet-txn-note" value={walletTxnForm.note} onChange={(e) => setWalletTxnForm({ ...walletTxnForm, note: e.target.value })} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-neutral-200 text-sm" placeholder="e.g., Late arrival — 2 jobs" />
          <button data-testid="wallet-txn-submit" onClick={submitWalletTxn} disabled={busy} className="mt-4 w-full py-2.5 rounded-lg bg-[#2E7D32] hover:bg-[#276D2A] disabled:opacity-50 text-white font-semibold text-sm">
            {busy ? 'Saving…' : 'Record adjustment'}
          </button>
        </Modal>
      )}

      {showAddEmp && (
        <Modal onClose={() => setShowAddEmp(false)} title="Add employee">
          <div className="grid grid-cols-2 gap-3">
            <TextField label="Full name" testid="emp-name" value={empForm.name} onChange={(v) => setEmpForm({ ...empForm, name: v })} />
            <TextField label="Designation" testid="emp-designation" value={empForm.designation} onChange={(v) => setEmpForm({ ...empForm, designation: v })} />
          </div>
          <div className="grid grid-cols-2 gap-3 mt-3">
            <TextField label="Base salary (₹)" testid="emp-base-salary" value={empForm.base_salary} onChange={(v) => setEmpForm({ ...empForm, base_salary: v.replace(/[^0-9]/g, '') })} numeric />
            <TextField label="PAN (optional)" testid="emp-pan" value={empForm.pan} onChange={(v) => setEmpForm({ ...empForm, pan: v.toUpperCase() })} />
          </div>
          <div className="grid grid-cols-2 gap-3 mt-3">
            <TextField label="Bank account" testid="emp-bank" value={empForm.bank_account} onChange={(v) => setEmpForm({ ...empForm, bank_account: v })} />
            <TextField label="Bank IFSC" testid="emp-ifsc" value={empForm.bank_ifsc} onChange={(v) => setEmpForm({ ...empForm, bank_ifsc: v.toUpperCase() })} />
          </div>
          <p className="mt-3 text-xs text-neutral-500">Statutory defaults apply: PF {payrollDefaults?.pf_pct}%, ESI {payrollDefaults?.esi_pct}%, TDS {payrollDefaults?.tds_pct}%. Overrides can be set later.</p>
          <button data-testid="emp-submit" onClick={addEmployee} disabled={busy} className="mt-4 w-full py-2.5 rounded-lg bg-[#2E7D32] hover:bg-[#276D2A] disabled:opacity-50 text-white font-semibold text-sm">
            {busy ? 'Saving…' : 'Add employee'}
          </button>
        </Modal>
      )}

      {showAddBank && (
        <Modal onClose={() => setShowAddBank(false)} title="Add bank / cash account">
          <label className="block text-xs font-semibold text-neutral-600">Type</label>
          <select data-testid="bank-form-type" value={bankForm.type} onChange={(e) => setBankForm({ ...bankForm, type: e.target.value })} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-neutral-200 text-sm">
            <option value="bank">Bank account</option>
            <option value="cash">Cash / Petty Cash</option>
          </select>
          <div className="mt-3">
            <TextField label="Name" testid="bank-form-name" value={bankForm.name} onChange={(v) => setBankForm({ ...bankForm, name: v })} />
          </div>
          {bankForm.type === 'bank' && (
            <div className="grid grid-cols-2 gap-3 mt-3">
              <TextField label="Account no." testid="bank-form-accno" value={bankForm.account_number} onChange={(v) => setBankForm({ ...bankForm, account_number: v.replace(/\s/g, '') })} />
              <TextField label="IFSC" testid="bank-form-ifsc" value={bankForm.ifsc} onChange={(v) => setBankForm({ ...bankForm, ifsc: v.toUpperCase() })} />
            </div>
          )}
          <div className="mt-3">
            <TextField label="Opening balance (₹)" testid="bank-form-opening" value={bankForm.opening_balance} onChange={(v) => setBankForm({ ...bankForm, opening_balance: v.replace(/[^0-9]/g, '') })} numeric />
          </div>
          <button data-testid="bank-form-submit" onClick={addBankAccount} disabled={busy} className="mt-4 w-full py-2.5 rounded-lg bg-[#2E7D32] hover:bg-[#276D2A] disabled:opacity-50 text-white font-semibold text-sm">
            {busy ? 'Saving…' : 'Add account'}
          </button>
        </Modal>
      )}

      {showAddVoucher && (
        <Modal onClose={() => setShowAddVoucher(false)} title={`New ${voucherForm.voucher_type} voucher`}>
          <div className="flex gap-2">
            <button data-testid="voucher-type-payment" onClick={() => setVoucherForm({ ...voucherForm, voucher_type: 'payment', category: 'rent' })} className={`flex-1 py-2 rounded-lg text-sm font-semibold ${voucherForm.voucher_type === 'payment' ? 'bg-red-100 text-red-700 border-2 border-red-500' : 'bg-white border border-neutral-200 text-neutral-500'}`}>
              Payment (money out)
            </button>
            <button data-testid="voucher-type-receipt" onClick={() => setVoucherForm({ ...voucherForm, voucher_type: 'receipt', category: 'cash_sale' })} className={`flex-1 py-2 rounded-lg text-sm font-semibold ${voucherForm.voucher_type === 'receipt' ? 'bg-[#E8F5E9] text-[#1B5E20] border-2 border-[#2E7D32]' : 'bg-white border border-neutral-200 text-neutral-500'}`}>
              Receipt (money in)
            </button>
          </div>
          <label className="mt-3 block text-xs font-semibold text-neutral-600">Bank / Cash account</label>
          <select data-testid="voucher-form-account" value={voucherForm.bank_account_id} onChange={(e) => setVoucherForm({ ...voucherForm, bank_account_id: e.target.value })} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-neutral-200 text-sm">
            <option value="">Select…</option>
            {bankAccounts.map((a) => <option key={a.id} value={a.id}>{a.name} ({a.type}) · ₹{a.current_balance.toLocaleString('en-IN')}</option>)}
          </select>
          <label className="mt-3 block text-xs font-semibold text-neutral-600">Category</label>
          {voucherForm.voucher_type === 'payment' ? (
            <select data-testid="voucher-form-category" value={voucherForm.category} onChange={(e) => setVoucherForm({ ...voucherForm, category: e.target.value })} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-neutral-200 text-sm">
              {EXPENSE_CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
          ) : (
            <input data-testid="voucher-form-category" value={voucherForm.category} onChange={(e) => setVoucherForm({ ...voucherForm, category: e.target.value })} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-neutral-200 text-sm" placeholder="cash_sale / deposit / vendor_refund" />
          )}
          <div className="grid grid-cols-2 gap-3 mt-3">
            <TextField label="Amount (₹)" testid="voucher-form-amount" value={voucherForm.amount} onChange={(v) => setVoucherForm({ ...voucherForm, amount: v.replace(/[^0-9]/g, '') })} numeric />
            <TextField label="Reference" testid="voucher-form-ref" value={voucherForm.reference} onChange={(v) => setVoucherForm({ ...voucherForm, reference: v })} />
          </div>
          <div className="mt-3">
            <TextField label={voucherForm.voucher_type === 'payment' ? 'Payee' : 'Payer'} testid="voucher-form-payee" value={voucherForm.payee} onChange={(v) => setVoucherForm({ ...voucherForm, payee: v })} />
          </div>
          <div className="mt-3">
            <label className="block text-[10px] font-semibold uppercase tracking-wider text-neutral-500">Note (optional)</label>
            <input data-testid="voucher-form-note" value={voucherForm.note} onChange={(e) => setVoucherForm({ ...voucherForm, note: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-neutral-200 text-sm" />
          </div>
          <button data-testid="voucher-form-submit" onClick={submitVoucher} disabled={busy} className="mt-4 w-full py-2.5 rounded-lg bg-[#2E7D32] hover:bg-[#276D2A] disabled:opacity-50 text-white font-semibold text-sm">
            {busy ? 'Saving…' : 'Record voucher'}
          </button>
        </Modal>
      )}
    </div>
  );
};

const Kpi = ({ label, value, icon: Icon, color, bg, testid }) => (
  <div data-testid={testid} className="bg-white border border-neutral-200 rounded-xl p-4">
    <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: bg }}>
      <Icon className="w-5 h-5" style={{ color }} />
    </div>
    <p className="mt-3 text-[11px] uppercase tracking-wider text-neutral-500 font-semibold">{label}</p>
    <p className="font-extrabold text-lg mt-0.5">{value}</p>
  </div>
);
const Th = ({ children, className = '' }) => <th className={`px-4 py-2.5 text-xs font-bold text-neutral-500 uppercase tracking-wider ${className}`}>{children}</th>;
const Card = ({ title, children, testid }) => (
  <div data-testid={testid} className="bg-white border border-neutral-200 rounded-2xl overflow-hidden">
    <div className="px-4 py-3 border-b border-neutral-100"><p className="font-bold text-sm">{title}</p></div>
    {children}
  </div>
);
const CalendarBanner = ({ start, end }) => (
  <><Calendar className="w-4 h-4 text-[#2E7D32]" /> <span className="font-bold text-[#1B5E20]">Week of {start}</span> <span className="text-neutral-500">→ {end}</span></>
);

const Pill = ({ children, color, bg }) => (
  <span className="text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wider" style={{ color, background: bg }}>{children}</span>
);

const CashFlowChart = ({ buckets }) => {
  const max = Math.max(1, ...buckets.map(b => Math.max(b.inflow || 0, b.outflow || 0)));
  if (!buckets.length) return <p className="px-4 py-8 text-center text-neutral-400 text-xs">No cash-flow activity in this window.</p>;
  return (
    <div className="px-4 py-4">
      <div className="flex items-end gap-1 h-32 border-b border-neutral-200">
        {buckets.map((b) => (
          <div key={b.date} className="flex-1 flex items-end gap-[1px] h-full" title={`${b.date}: +₹${b.inflow.toLocaleString('en-IN')} / −₹${b.outflow.toLocaleString('en-IN')} / net ₹${b.net.toLocaleString('en-IN')}`}>
            <div className="flex-1 bg-[#2E7D32] rounded-t" style={{ height: `${Math.max(2, (b.inflow / max) * 100)}%` }} />
            <div className="flex-1 bg-red-500 rounded-t" style={{ height: `${Math.max(2, (b.outflow / max) * 100)}%` }} />
          </div>
        ))}
      </div>
      <div className="mt-2 flex items-center justify-between text-[10px] text-neutral-500">
        <span>{buckets[0]?.date}</span>
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1"><span className="w-2 h-2 bg-[#2E7D32] rounded-sm"></span> Inflow</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 bg-red-500 rounded-sm"></span> Outflow</span>
        </div>
        <span>{buckets[buckets.length - 1]?.date}</span>
      </div>
    </div>
  );
};

const TextField = ({ label, value, onChange, testid, numeric = false }) => (
  <div>
    <label className="block text-[10px] font-semibold uppercase tracking-wider text-neutral-500">{label}</label>
    <input
      data-testid={testid}
      type="text" inputMode={numeric ? 'numeric' : 'text'}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="mt-1 w-full px-3 py-2 rounded-lg border border-neutral-200 text-sm focus:outline-none focus:border-[#2E7D32]"
    />
  </div>
);

const MiniBar = ({ rows }) => {  const max = Math.max(1, ...rows.map((r) => r.amount || 0));
  return (
    <div className="px-4 py-3">
      <div className="flex items-end gap-1 h-24">
        {rows.map((r) => (
          <div key={r.date} className="flex-1 flex flex-col items-center">
            <div className="w-full bg-[#2E7D32] rounded-t" style={{ height: `${Math.max(2, (r.amount / max) * 100)}%` }} title={`${r.date}: ₹${r.amount}`} />
          </div>
        ))}
        {rows.length === 0 && <p className="text-center text-neutral-400 text-xs w-full">No data</p>}
      </div>
      {rows.length > 0 && (
        <p className="mt-2 text-[10px] text-neutral-500 text-center">{rows[0].date} → {rows[rows.length - 1].date}</p>
      )}
    </div>
  );
};
const Modal = ({ children, onClose, title }) => (
  <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
    <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-md p-5">
      <div className="flex items-center justify-between mb-4">
        <p className="font-extrabold">{title}</p>
        <button onClick={onClose} className="text-neutral-500 hover:text-neutral-900"><X className="w-5 h-5" /></button>
      </div>
      {children}
    </div>
  </div>
);

export default FranchisePortal;
